/**
 * The video rules of each social platform: the file type, the file size, the length and the shape
 * that a platform takes for a video post. The Composer (music24-app) shows the refusal next to the
 * channel, and music24-api refuses the schedule with the same rule (`assertPostMediaFits`), so a
 * video never fails at the hour of its post. The file has no imports, so every project can use it.
 *
 * Some limits come from our publishers, not from the platform:
 * - TikTok: `TikTokPublisher` sends the file in one piece, and TikTok takes one piece of at most
 *   64 MB (`TIKTOK_VIDEO_MAX_BYTES`). TikTok itself takes 3 s to 10 min.
 * - YouTube: `YouTubePublisher` holds the file in memory, at most 200 MB
 *   (`PUSHED_VIDEO_MAX_BYTES`, the same cap as `MAX_PUSHED_MEDIA_BYTES` of music24-api).
 * The other platforms pull the file from our public file route:
 * - Instagram (Graph API): a Reel is 3 s to 15 min and at most 300 MB; a story video is 3 s to
 *   60 s and at most 100 MB. MP4 or MOV, a shape from 0.01:1 to 10:1.
 * - Facebook (`/{page}/videos` with `file_url`): at most 1 GB and 20 min. A vertical video of 3 to
 *   90 s goes out as a Reel (`socialPostFormat.ts`).
 * - Threads: at most 1 GB and 5 min. MP4 or MOV, a shape from 0.01:1 to 10:1.
 *
 * The upload of the Composer takes a video up to `POST_VIDEO_UPLOAD_MAX_BYTES` (1 GB): the largest
 * file that one of the platforms takes. These rules then refuse it per channel.
 */

export type PostVideoPlatform = 'tiktok' | 'instagram' | 'youtube' | 'facebook' | 'threads' | 'x';

export type PostVideoFormat = 'post' | 'story';

const MB = 1024 * 1024;

/** The largest video that the Composer upload takes (1 GB): the largest that a platform takes. */
export const POST_VIDEO_UPLOAD_MAX_BYTES = 1024 * MB;

/** The video types that the Composer upload takes. Every platform takes MP4 and MOV. */
export const POST_VIDEO_UPLOAD_TYPES: ReadonlyArray<string> = ['video/mp4', 'video/quicktime'];

/** TikTok takes one piece of at most 64 MB, and `TikTokPublisher` sends the file in one piece. */
export const TIKTOK_VIDEO_MAX_BYTES = 64 * MB;

/** Instagram takes a Reel of at most 300 MB. The full-song video render keeps its file under it. */
export const INSTAGRAM_REEL_MAX_BYTES = 300 * MB;

/** The largest file that a publisher holds in memory to push it (YouTube, TikTok). */
export const PUSHED_VIDEO_MAX_BYTES = 200 * MB;

export interface PostVideo {
  /** The MIME type; null when not known (the rule then checks no type). */
  mimeType: string | null;
  /** Bytes; null when not known (the rule then checks no size). */
  sizeBytes: number | null;
  /** Seconds; null when not known (the rule then checks no length). */
  durationSeconds: number | null;
  /** Pixels; null when not known (the rule then checks no shape). */
  width: number | null;
  height: number | null;
}

export type PostVideoRefusal =
  | { reason: 'type'; mimeType: string; allowed: ReadonlyArray<string> }
  | { reason: 'size'; sizeBytes: number; maxBytes: number }
  | { reason: 'tooShort'; durationSeconds: number; minSeconds: number }
  | { reason: 'tooLong'; durationSeconds: number; maxSeconds: number }
  | { reason: 'aspect'; ratio: number; minRatio: number; maxRatio: number };

interface VideoLimits {
  maxBytes?: number;
  minSeconds?: number;
  maxSeconds?: number;
  ratio?: { min: number; max: number };
}

interface PlatformVideoRule {
  types: ReadonlyArray<string>;
  post: VideoLimits;
  /** The limits of a story. Absent: the post limits. */
  story?: VideoLimits;
}

const META_RATIO = { min: 0.01, max: 10 };

const RULES: Record<PostVideoPlatform, PlatformVideoRule> = {
  instagram: {
    types: POST_VIDEO_UPLOAD_TYPES,
    post: { maxBytes: INSTAGRAM_REEL_MAX_BYTES, minSeconds: 3, maxSeconds: 15 * 60, ratio: META_RATIO },
    story: { maxBytes: 100 * MB, minSeconds: 3, maxSeconds: 60, ratio: META_RATIO },
  },
  tiktok: {
    types: POST_VIDEO_UPLOAD_TYPES,
    post: { maxBytes: TIKTOK_VIDEO_MAX_BYTES, minSeconds: 3, maxSeconds: 10 * 60 },
  },
  youtube: {
    types: POST_VIDEO_UPLOAD_TYPES,
    post: { maxBytes: PUSHED_VIDEO_MAX_BYTES },
  },
  facebook: {
    types: POST_VIDEO_UPLOAD_TYPES,
    post: { maxBytes: POST_VIDEO_UPLOAD_MAX_BYTES, maxSeconds: 20 * 60 },
  },
  threads: {
    types: POST_VIDEO_UPLOAD_TYPES,
    post: { maxBytes: POST_VIDEO_UPLOAD_MAX_BYTES, maxSeconds: 5 * 60, ratio: META_RATIO },
  },
  // X takes 512 MB and 0.5 s to 140 s. `XPublisher` holds the file in memory to upload it, so the
  // size limit is the one of the other pushed uploads.
  x: {
    types: ['video/mp4'],
    post: { maxBytes: PUSHED_VIDEO_MAX_BYTES, minSeconds: 0.5, maxSeconds: 140 },
  },
};

function normalType(mimeType: string): string {
  return mimeType.toLowerCase().split(';')[0]?.trim() ?? '';
}

/**
 * Why `platform` refuses this video in this format, or null when it takes it (or when nothing is
 * known that it could refuse). The first broken rule wins: type, size, length, then shape.
 */
export function postVideoRefusal(platform: string, format: PostVideoFormat, video: PostVideo): PostVideoRefusal | null {
  const rule = RULES[platform as PostVideoPlatform];
  if (rule === undefined) {
    return null;
  }
  const limits = format === 'story' ? (rule.story ?? rule.post) : rule.post;
  if (video.mimeType !== null && !rule.types.includes(normalType(video.mimeType))) {
    return { reason: 'type', mimeType: video.mimeType, allowed: rule.types };
  }
  if (limits.maxBytes !== undefined && video.sizeBytes !== null && video.sizeBytes > limits.maxBytes) {
    return { reason: 'size', sizeBytes: video.sizeBytes, maxBytes: limits.maxBytes };
  }
  const duration = video.durationSeconds;
  if (duration !== null && duration > 0) {
    if (limits.minSeconds !== undefined && duration < limits.minSeconds) {
      return { reason: 'tooShort', durationSeconds: duration, minSeconds: limits.minSeconds };
    }
    if (limits.maxSeconds !== undefined && duration > limits.maxSeconds) {
      return { reason: 'tooLong', durationSeconds: duration, maxSeconds: limits.maxSeconds };
    }
  }
  const { width, height } = video;
  if (limits.ratio !== undefined && width !== null && height !== null && width > 0 && height > 0) {
    const ratio = width / height;
    if (ratio < limits.ratio.min || ratio > limits.ratio.max) {
      return { reason: 'aspect', ratio, minRatio: limits.ratio.min, maxRatio: limits.ratio.max };
    }
  }
  return null;
}

/** "64 MB", "1 GB": a byte limit for a sentence. */
export function byteLimitLabel(bytes: number): string {
  if (bytes >= 1024 * MB && bytes % (1024 * MB) === 0) {
    return `${bytes / (1024 * MB)} GB`;
  }
  return `${Math.round(bytes / MB)} MB`;
}

/** "3 s", "60 s", "15 min": a length limit for a sentence. */
export function secondsLimitLabel(seconds: number): string {
  if (seconds >= 120 && seconds % 60 === 0) {
    return `${seconds / 60} min`;
  }
  return `${seconds} s`;
}
